import { SsoService } from './services/sso.service';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxLogglyModule } from 'ngx-loggly-logger';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppConfig } from './app.config';
import { AppRouting } from './app.routing';
import { ComponentsModule } from './components/components.module';
import { DirectivesModule } from './directives/directives.module';
import { CanActivateAuthGuard } from './guards/can-activate-auth-guard';
import { ConfirmDeactivateGuard } from './guards/confirm-deactivate-guard';
import { PermissionGuard } from './guards/permission-guard.service';
import { PipesModule } from './pipes/pipes.module';
import { AddressService } from './services/address.service';
import { AppService } from './services/app.service';
import { AuthenticationService } from './services/authentication.service';
import { ConfigService } from './services/config.service';
import { ContractStopService } from './services/contract-stop.service';
import { CustomerService } from './services/customer.service';
import { CustomizationService } from './services/customization.service';
import { DateTimeService } from './services/date-time.service';
import { DriverLocationService } from './services/driver-location.service';
import { ErrorService } from './services/error.service';
import { FileService } from './services/file.service';
import { GlobalService } from './services/global.service';
import { Interceptor } from './services/http-interceptor.service';
import { InvoicesService } from './services/invoices.service';
import { ModalService } from './services/modal.service';
import { NewLoginService } from './services/new-login.service';
import { OrderTemplateService } from './services/order-template.service';
import { OrderService } from './services/order.service';
import { PushService } from './services/push.service';
import { ReportsService } from './services/reports.service';
import { RouteTrackingService } from './services/route-tracking.service';
import { SettlementService } from './services/settlement.service';
import { SnackbarService } from './services/snackbar.service';
import { SortingService } from './services/sorting.service';
import { SpinnerService } from './services/spinner.service';
import { SubscriptionService } from './services/subscription.service';
import { TrackOrderService } from './services/track-order.service';
import { TrackParcelService } from './services/track-parcel.service';
import { UserService } from './services/user.service';
import { ValidatorService } from './services/validator.service';
import { WorkService } from './services/work.service';
import { WorkerService } from './services/worker.service';
import { MaterialModule } from './shared/material.module';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DefaultUrlSerializer, UrlTree, UrlSerializer } from '@angular/router';

export function httpFactory(authenticationService: AuthenticationService) {
	return new Interceptor(authenticationService);
}

export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
	parse(url: string): UrlTree {
		//***
		//    If there is any query parameters
		//    in the URL we will skip the lowercase conversion
		//    for the URL and just convert the main domain to the lowercase.
		//***
		if (url.indexOf('?') === -1 && url.indexOf('&') === -1 && url.indexOf(';') === -1) {
			return super.parse(url.toLowerCase());
		} else {
			if (url.indexOf('?') != -1) {
				let splitUrl = url.split('?');
				url = `${splitUrl[0].toLowerCase()}?${splitUrl.splice(1, splitUrl.length - 1).join('?')}`;
			}
			if (url.indexOf(';') != -1) {
				let splitUrl = url.split(';');
				url = `${splitUrl[0].toLowerCase()};${splitUrl.splice(1, splitUrl.length - 1).join(';')}`;
			}
			if (url.indexOf('&') != -1) {
				let splitUrl = url.split('&');
				url = `${splitUrl[0].toLowerCase()}&${splitUrl.splice(1, splitUrl.length - 1).join('&')}`;
			}

      return	super.parse(url);
		}
	}
}

export class CustomLoader implements TranslateLoader {

	constructor(private _http: HttpClient, private _customizationService: CustomizationService) {
	}

	public getTranslation(lang: String): Observable<any> {
		switch (lang) {
			case 'DEFAULT':
				return this._http.get<any>('customizations/languages/standard.json');
			case 'CUSTOM':
				return this._customizationService.getCustomizedTranslations(environment.siteId);
		}
	}
}

export function initConfig(config: AppConfig) {
	return () => config.load();
}

@NgModule({
	bootstrap: [
		AppComponent
	],
	providers: [
		AddressService,
		AppConfig,
		{
			provide: APP_INITIALIZER,
			useFactory: initConfig,
			deps: [AppConfig],
			multi: true
		},
		AppService,
		AuthenticationService,
		CanActivateAuthGuard,
		PermissionGuard,
		ConfirmDeactivateGuard,
		CustomerService,
		DateTimeService,
		DriverLocationService,
		ErrorService,
		FileService,
		GlobalService,
		{
			provide: HTTP_INTERCEPTORS,
			useFactory: httpFactory,
			deps: [AuthenticationService],
			multi: true
		},
		{
			provide: UrlSerializer,
			useClass: LowerCaseUrlSerializer
		},
		InvoicesService,
		{provide: LocationStrategy, useClass: HashLocationStrategy},
		ModalService,
		NewLoginService,
		OrderService,
		OrderTemplateService,
		PushService,
		ReportsService,
		RouteTrackingService,
		SettlementService,
		SortingService,
		TrackOrderService,
		TrackParcelService,
		UserService,
		ValidatorService,
		WorkerService,
		WorkService,
		SubscriptionService,
		SpinnerService,
		SnackbarService,
		ConfigService,
		ContractStopService,
		CustomizationService,
		SsoService
	],
	declarations: [
		// Components / Directives/ Pipes
		AppComponent
	],
	imports: [
		BrowserAnimationsModule,
		BrowserModule,
		ComponentsModule,
		DirectivesModule,
		HttpClientModule,
		MaterialModule,
		NgxLogglyModule.forRoot(),
		PipesModule,
		RouterModule.forRoot(AppRouting, {useHash: true, preloadingStrategy: PreloadAllModules, relativeLinkResolution: 'legacy'}),
		ServiceWorkerModule.register('sw-master.js', {enabled: environment.production}),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useClass: CustomLoader,
				deps: [HttpClient, CustomizationService]
			}
		}),
    BrowserModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatAutocompleteModule
	]
})
export class AppModule {
}

